// Admin console: Sumber Pengetahuan (/admin/knowledge-source/*). Pluto reads
// the folder Basis Pengetahuan in Google Drive through a Google service
// account: the admin saves its JSON key here, shares the folder with the
// service account's email as a Viewer, and picks that folder.
//   header     plain spacing, hairline below   h1, one sentence
//   note       quiet line                      Client ID & Secret for user login live under Login Google
//   banner     notice (warning)                only when the server has no encryption key
//   status     card (form-card)                Siap / the reason, Tes koneksi (file count)
//   step 1     card (form-card)                "Kunci service account": file input or paste, Simpan kunci;
//                                              once stored: the email to share with (big, Salin), project,
//                                              key id tail, "tersimpan", Ganti / Hapus (Hapus confirmed)
//   step 2     card (form-card)                "Folder Basis Pengetahuan": picker dialog (folders shared with
//                                              the service account, search, subfolders, breadcrumb, empty state
//                                              explaining how to share), enabled once the key is saved
//   how-to     card                            the Google Cloud steps
//   history    rows                            who, when, what, IP
// The key is write-only: never shown back, only client_email, project_id and
// the last 6 characters of the key id. A 400 shows its message under the key
// input; a 503 encryption_unavailable shows the banner.
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import * as API from "../api";
import type { KnowledgeFolderItem, KnowledgeFolderList, KnowledgeSource, KnowledgeSourceField, KnowledgeSourceReason, KnowledgeSourceStatus, KnowledgeSourceTestResult } from "../api";
import { formatDateTime } from "../lib/format";
import { Badge, Dialog, EmptyState, Field, fieldAria, Notice, PageHeader, SearchInput, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

export const FIELD_LABEL: Record<KnowledgeSourceField, string> = {
  saKey: "kunci service account",
  saKeyRemoved: "kunci service account (dihapus)",
  knowledgeFolder: "folder Basis Pengetahuan",
  clientId: "Client ID (lama)",
  clientSecret: "Client secret (lama)",
  driveFolderId: "folder Pluto (lama)",
  driveConnected: "koneksi Google Drive admin (lama, dihubungkan)",
  driveDisconnected: "koneksi Google Drive admin (lama, diputus)",
  driveRootFolder: "folder Pluto di Drive (lama)",
  driveUploadFolderId: "folder unggahan admin (lama)",
  driveFolderCleared: "ID folder Drive yang berisi Client Secret (dihapus otomatis)",
};

export const LOGIN_NOTE = "Client ID & Secret untuk login user diatur di menu Login Google.";
export const SHARE_INSTRUCTION = "Bagikan folder Basis Pengetahuan di Google Drive ke email ini dengan akses Pelihat (Viewer).";
export const ENCRYPTION_BANNER = "Kunci enkripsi belum diatur di server; kunci service account tidak dapat disimpan.";
export const LEAKED_SECRET_MESSAGE = "Itu terlihat seperti Client Secret, bukan ID folder.";
export const KEY_MAX_BYTES = 10 * 1024;
export const NO_KEY_HINT = "Simpan kunci service account dulu, lalu pilih folder.";

export const REASON_TEXT: Record<KnowledgeSourceReason, string> = {
  no_key: "Kunci service account belum disimpan.",
  no_folder: "Folder Basis Pengetahuan belum dipilih.",
  key_rejected: "Kunci service account ditolak Google. Ganti kuncinya dengan kunci JSON yang baru.",
  folder_unreadable: "Folder Basis Pengetahuan tidak dapat dibaca. Pastikan folder masih dibagikan ke email service account.",
  unavailable: "Google Drive tidak dapat dihubungi saat ini. Coba lagi sebentar lagi.",
};

/** Same rules as the API: never a client secret, else a Drive id of 10+ safe characters. */
export function folderIdError(value: string): string | null {
  const v = value.trim();
  if (/^GOCSPX-/i.test(v)) return LEAKED_SECRET_MESSAGE;
  if (!/^[A-Za-z0-9_-]{10,200}$/.test(v)) return "ID folder Google Drive tidak valid (minimal 10 karakter: huruf, angka, tanda hubung, atau garis bawah).";
  return null;
}

/** Clipboard access; tests replace `write`. */
export const clipboard = {
  async write(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  },
};

function failureText(err: unknown, what: string): string {
  if (err instanceof API.ApiError && err.status === 429) {
    return err.retryAfter ? `Terlalu banyak ${what}. Coba lagi dalam ${err.retryAfter} detik.` : API.errorMessage(err);
  }
  return API.errorMessage(err);
}

function lastChanged(source: KnowledgeSource): string {
  if (!source.updatedAt) return "Belum pernah disimpan.";
  return `Terakhir diubah oleh ${source.updatedBy?.name ?? "Pengguna tidak dikenal"}, ${formatDateTime(source.updatedAt)}.`;
}

/** The status block: Siap or the reason, and Tes koneksi. */
function StatusCard({ status, onTested }: { status: ReturnType<typeof useAsync<KnowledgeSourceStatus>>; onTested: () => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<KnowledgeSourceTestResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function test() {
    if (busy) return;
    setBusy(true);
    setResult(null);
    setFailure(null);
    try {
      setResult(await API.testKnowledgeSource());
      status.reload();
      onTested();
    } catch (err) {
      setFailure(failureText(err, "permintaan"));
    } finally {
      setBusy(false);
    }
  }

  const s = status.data;
  return (
    <section className="card form-card ks-status" aria-labelledby="ks-status-title">
      <h2 id="ks-status-title" className="card-title">
        Status
      </h2>
      {status.error ? (
        <Notice
          tone="danger"
          title="Status belum termuat"
          action={
            <button type="button" className="btn btn-secondary" onClick={() => status.reload()}>
              Coba lagi
            </button>
          }
        >
          <p>{API.errorMessage(status.error)}</p>
        </Notice>
      ) : !s ? (
        <SkeletonRows count={1} label="Memuat status" />
      ) : (
        <div className="ks-drive-status" role="group" aria-label="Status sumber pengetahuan">
          <Badge tone={s.ready ? "success" : s.reason === "no_key" || s.reason === "no_folder" ? "neutral" : "warning"}>{s.ready ? "Siap" : "Belum siap"}</Badge>
          <span className="ks-drive-line">{s.ready ? "Pluto dapat membaca folder Basis Pengetahuan." : REASON_TEXT[s.reason ?? "no_key"]}</span>
        </div>
      )}
      {result ? (
        <Notice tone={result.ok ? "success" : "danger"} title={result.ok ? "Koneksi berfungsi" : "Koneksi bermasalah"}>
          <p>{result.message}</p>
        </Notice>
      ) : null}
      {failure ? (
        <Notice tone="danger" title="Permintaan belum berhasil">
          <p>{failure}</p>
        </Notice>
      ) : null}
      <div className="button-row">
        <button type="button" className="btn btn-secondary" onClick={test} disabled={busy} aria-busy={busy || undefined}>
          {busy ? "Menguji" : "Tes koneksi"}
        </button>
      </div>
    </section>
  );
}

/** Step 1: the JSON key (file or paste), then the email to share the folder with. */
function KeyCard({ saved, onSaved, onEncryptionMissing }: { saved: KnowledgeSource; onSaved: (next: KnowledgeSource) => void; onEncryptionMissing: () => void }) {
  const [replacing, setReplacing] = useState(false);
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState<"save" | "remove" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState<boolean | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const key = saved.key;
  const encryption = saved.encryptionAvailable;
  const formOpen = !key || replacing;

  function resetForm() {
    setText("");
    setFileName(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function pickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.currentTarget.files?.[0];
    setError(null);
    if (!file) return;
    if (file.size > KEY_MAX_BYTES) {
      setError("File kunci terlalu besar (maksimal 10 KB). Pilih file JSON dari Google Cloud.");
      return;
    }
    setFileName(file.name);
    setText(await file.text());
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setFailure(null);
    if (!text.trim()) {
      setError("Pilih file kunci JSON atau tempel isinya.");
      document.getElementById("ks-key-file")?.focus();
      return;
    }
    setBusy("save");
    try {
      onSaved(await API.saveServiceAccountKey(text));
      resetForm();
      setReplacing(false);
    } catch (err) {
      if (err instanceof API.ApiError && err.status === 400) {
        setError(API.serviceAccountKeyError(err.message));
        document.getElementById("ks-key-text")?.focus();
      } else if (err instanceof API.ApiError && err.status === 503 && err.body?.code === "encryption_unavailable") {
        setError(err.message);
        onEncryptionMissing();
      } else setFailure(failureText(err, "perubahan"));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy("remove");
    setFailure(null);
    try {
      onSaved(await API.removeServiceAccountKey());
      setConfirming(false);
      resetForm();
    } catch (err) {
      setConfirming(false);
      setFailure(failureText(err, "perubahan"));
    } finally {
      setBusy(null);
    }
  }

  async function copy() {
    if (!key) return;
    setCopied(await clipboard.write(key.clientEmail));
  }

  return (
    <section className="card form-card ks-key" aria-labelledby="ks-key-title">
      <h2 id="ks-key-title" className="card-title">
        1. Kunci service account
      </h2>
      {failure ? (
        <Notice tone="danger" title="Kunci belum tersimpan">
          <p>{failure}</p>
        </Notice>
      ) : null}
      {key ? (
        <div className="ks-share">
          <p className="field-label ks-label" id="ks-email-label">
            Email service account
          </p>
          <div className="ks-email-row">
            <span className="ks-email" aria-labelledby="ks-email-label">
              {key.clientEmail}
            </span>
            <button type="button" className="btn btn-secondary ks-copy" onClick={copy}>
              <Glyph name={copied ? "check" : "documentText"} />
              {copied ? "Tersalin" : "Salin"}
            </button>
          </div>
          <p className="ks-share-text">{SHARE_INSTRUCTION}</p>
          {copied === false ? <p className="field-hint">Salin manual: pilih email di atas, lalu salin.</p> : null}
          <div className="ks-stored" role="group" aria-label="Kunci tersimpan">
            <span className="ks-mask">Project {key.projectId ?? "tidak diketahui"}</span>
            <span className="ks-mask" aria-label={`ID kunci berakhiran ${key.keyIdSuffix ?? ""}`}>
              ID kunci ••••{key.keyIdSuffix}
            </span>
            <Badge tone="success">tersimpan</Badge>
          </div>
          {!formOpen ? (
            <div className="button-row">
              <button type="button" className="btn btn-secondary" onClick={() => setReplacing(true)} disabled={busy !== null || !encryption}>
                Ganti
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirming(true)} disabled={busy !== null}>
                Hapus
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="quiet-line ks-meta">
          <Glyph name="infoCircle" />
          <span>Unggah file kunci JSON dari Google Cloud, atau tempel isinya. Kunci disimpan terenkripsi dan tidak pernah ditampilkan lagi.</span>
        </p>
      )}
      {formOpen ? (
        <form className="form-stack" noValidate onSubmit={submit}>
          <div className="field">
            <label className="field-label" htmlFor="ks-key-file">
              File kunci JSON
            </label>
            <input
              ref={fileRef}
              id="ks-key-file"
              type="file"
              accept=".json,application/json"
              className="ks-file"
              disabled={!encryption || busy !== null}
              onChange={pickFile}
              aria-describedby="ks-key-file-hint"
            />
            <p className="field-hint" id="ks-key-file-hint">
              {fileName ? `Dipilih: ${fileName}` : "Maksimal 10 KB."}
            </p>
          </div>
          <Field id="ks-key-text" label="Atau tempel isi file JSON" error={error ?? undefined} hint={!encryption ? "Belum dapat diisi sampai kunci enkripsi diatur di server." : "Isinya diawali { \"type\": \"service_account\" ..."}>
            <textarea
              {...fieldAria("ks-key-text", error)}
              className="ks-key-text"
              rows={5}
              spellCheck={false}
              autoCapitalize="none"
              autoComplete="off"
              value={fileName ? "" : text}
              placeholder={fileName ? "Isi diambil dari file yang dipilih." : undefined}
              disabled={!encryption || Boolean(fileName)}
              readOnly={busy !== null}
              onChange={(e) => setText(e.currentTarget.value)}
            />
          </Field>
          <div className="button-row">
            {replacing ? (
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy !== null}
                onClick={() => {
                  setReplacing(false);
                  resetForm();
                }}
              >
                Batal
              </button>
            ) : null}
            <button type="submit" className="btn" disabled={!encryption} aria-busy={busy === "save" || undefined}>
              {busy === "save" ? "Memeriksa" : "Simpan kunci"}
            </button>
          </div>
        </form>
      ) : null}
      <p className="quiet-line ks-meta">
        <Glyph name="clock" />
        <span>{lastChanged(saved)}</span>
      </p>
      <Dialog
        open={confirming}
        busy={busy === "remove"}
        size="sm"
        title="Hapus kunci service account?"
        description="Pluto berhenti membaca folder Basis Pengetahuan sampai kunci baru disimpan."
        onClose={() => setConfirming(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirming(false)} disabled={busy === "remove"}>
              Batal
            </button>
            <button type="button" className="btn" onClick={remove} aria-busy={busy === "remove" || undefined}>
              {busy === "remove" ? "Menghapus" : "Ya, hapus"}
            </button>
          </>
        }
      >
        <p className="ks-dialog-text">Pengetahuan yang sudah dibuat dari folder tetap ada. Hapus juga kuncinya di Google Cloud bila tidak dipakai lagi.</p>
      </Dialog>
    </section>
  );
}

/** The picker dialog: folders shared with the service account, search, breadcrumb, subfolders, a manual id fallback. */
function FolderPicker({ open, email, onClose, onChosen }: { open: boolean; email: string; onClose: () => void; onChosen: (next: KnowledgeSource) => void }) {
  const [parent, setParent] = useState(API.SHARED_FOLDERS);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [list, setList] = useState<KnowledgeFolderList | null>(null);
  const [more, setMore] = useState<KnowledgeFolderItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manual, setManual] = useState(false);
  const [manualId, setManualId] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (open) setParent(API.SHARED_FOLDERS);
  }, [open]);

  // Debounced search: typing waits 300ms before asking Drive.
  useEffect(() => {
    const t = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    API.listKnowledgeFolders({ parent, q: term || undefined }).then(
      (res) => {
        if (mine !== seq.current) return;
        setList(res);
        setMore([]);
        setLoading(false);
      },
      (err) => {
        if (mine !== seq.current) return;
        setError(API.errorMessage(err));
        setLoading(false);
      },
    );
  }, [open, parent, term, reloadKey]);

  async function loadMore() {
    if (!list?.nextPageToken || loading) return;
    setLoading(true);
    try {
      const next = await API.listKnowledgeFolders({ parent, q: term || undefined, pageToken: list.nextPageToken });
      setMore((m) => [...m, ...next.folders]);
      setList({ ...list, nextPageToken: next.nextPageToken });
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function enter(id: string) {
    setSearch("");
    setTerm("");
    setParent(id);
  }

  async function choose(folderId: string): Promise<boolean> {
    if (saving) return false;
    setSaving(true);
    setError(null);
    try {
      onChosen(await API.chooseKnowledgeFolder(folderId));
      return true;
    } catch (err) {
      setError(err instanceof API.ApiError && err.status === 400 ? API.serviceAccountKeyError(err.message) : API.errorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function saveManual(e: FormEvent) {
    e.preventDefault();
    const problem = folderIdError(manualId);
    setManualError(problem);
    if (problem) {
      document.getElementById("ks-folder-manual")?.focus();
      return;
    }
    if (await choose(manualId.trim())) {
      setManual(false);
      setManualId("");
    }
  }

  const folders = [...(list?.folders ?? []), ...more];
  const searching = Boolean(list?.query);
  const here = list && !searching ? list.parent : null;
  const atTop = parent === API.SHARED_FOLDERS;
  const canPickHere = here !== null && here.id !== API.SHARED_FOLDERS;
  const crumbs = list?.path.length ? list.path : [{ id: API.SHARED_FOLDERS, name: "Dibagikan ke service account" }];

  return (
    <Dialog
      open={open}
      busy={saving}
      size="md"
      title="Pilih folder Basis Pengetahuan"
      description="Folder yang dibagikan ke email service account. Buka folder, lalu pilih."
      onClose={onClose}
      footer={
        <button type="button" className="btn" onClick={() => here && choose(here.id)} disabled={!canPickHere || saving} aria-busy={saving || undefined}>
          {saving ? "Menyimpan" : "Pilih folder ini"}
        </button>
      }
    >
      <div className="ks-picker">
        <SearchInput id="ks-folder-search" value={search} onChange={setSearch} label="Cari folder" placeholder="Cari folder yang dibagikan" />
        {searching ? (
          <p className="quiet-line ks-meta">Hasil pencarian untuk “{list?.query}”. Pilih folder untuk membukanya.</p>
        ) : (
          <nav aria-label="Lokasi folder">
            <ol className="ks-crumbs">
              {crumbs.map((crumb, i, all) => (
                <li key={crumb.id}>
                  {i === all.length - 1 ? (
                    <span className="ks-crumb-current" aria-current="location">
                      {crumb.name}
                    </span>
                  ) : (
                    <button type="button" className="btn btn-ghost ks-crumb" onClick={() => enter(crumb.id)}>
                      {crumb.name}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
        {error ? (
          <Notice tone="danger" title="Folder belum dapat dipakai">
            <p>{error}</p>
          </Notice>
        ) : null}
        {loading && !list ? (
          <SkeletonRows count={3} label="Memuat folder" />
        ) : folders.length === 0 && !loading && !error ? (
          atTop && !searching ? (
            <EmptyState
              icon="folder"
              title="Belum ada folder yang dibagikan"
              body={`Buka Google Drive, klik kanan folder Basis Pengetahuan, pilih Bagikan, lalu tambahkan ${email} dengan akses Pelihat (Viewer). Setelah itu muat ulang daftar ini.`}
              action={
                <button type="button" className="btn btn-secondary" onClick={() => setReloadKey((k) => k + 1)}>
                  Muat ulang
                </button>
              }
            />
          ) : (
            <p className="quiet-line ks-meta">{searching ? "Tidak ada folder yang cocok." : "Tidak ada subfolder di sini."}</p>
          )
        ) : (
          <ul className="rows ks-folder-list" aria-label="Folder" aria-busy={loading || undefined}>
            {folders.map((f) => (
              <li key={f.id}>
                <button type="button" className="ks-folder-row" onClick={() => enter(f.id)}>
                  <Glyph name="folder" />
                  <span className="ks-folder-name">{f.name}</span>
                  <Glyph name="chevronRight" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {list?.nextPageToken ? (
          <div className="button-row">
            <button type="button" className="btn btn-secondary" onClick={loadMore} disabled={loading}>
              Muat lagi
            </button>
          </div>
        ) : null}
        {manual ? (
          <form className="form-stack ks-manual" noValidate onSubmit={saveManual}>
            <Field id="ks-folder-manual" label="ID folder Drive" error={manualError ?? undefined} hint="Bagian akhir alamat folder di Google Drive.">
              <input
                {...fieldAria("ks-folder-manual", manualError)}
                autoComplete="off"
                spellCheck={false}
                autoCapitalize="none"
                maxLength={200}
                value={manualId}
                readOnly={saving}
                onChange={(e) => setManualId(e.currentTarget.value)}
              />
            </Field>
            <div className="button-row">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={saving}
                onClick={() => {
                  setManual(false);
                  setManualError(null);
                }}
              >
                Batal
              </button>
              <button type="submit" className="btn btn-secondary" aria-busy={saving || undefined}>
                {saving ? "Memeriksa" : "Simpan ID"}
              </button>
            </div>
          </form>
        ) : (
          <div className="button-row">
            <button type="button" className="btn btn-ghost ks-manual-link" onClick={() => setManual(true)}>
              Masukkan ID manual
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** Step 2: the folder, picked once the key is saved. */
function FolderCard({ saved, onChosen }: { saved: KnowledgeSource; onChosen: (next: KnowledgeSource) => void }) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const folder = saved.folder;
  const key = saved.key;

  async function clear() {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      onChosen(await API.chooseKnowledgeFolder(null));
    } catch (err) {
      setFailure(failureText(err, "perubahan"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card form-card ks-folder" aria-labelledby="ks-folder-title">
      <h2 id="ks-folder-title" className="card-title">
        2. Folder Basis Pengetahuan
      </h2>
      {failure ? (
        <Notice tone="danger" title="Folder belum tersimpan">
          <p>{failure}</p>
        </Notice>
      ) : null}
      <div className="field">
        <p className="field-label ks-label" id="ks-folder-pick-label">
          Folder Basis Pengetahuan
        </p>
        <button
          type="button"
          id="ks-folder-pick"
          className="ks-folder-select"
          aria-haspopup="dialog"
          aria-labelledby="ks-folder-pick-label ks-folder-pick-current"
          aria-describedby="ks-folder-pick-hint"
          disabled={!key}
          onClick={() => setPicking(true)}
        >
          <Glyph name="folder" />
          <span className="ks-folder-current" id="ks-folder-pick-current">
            {folder ? (folder.path ?? folder.name ?? folder.id) : "Belum dipilih"}
          </span>
          <Glyph name="chevronRight" />
        </button>
        <p className="field-hint" id="ks-folder-pick-hint">
          {!key
            ? NO_KEY_HINT
            : folder
              ? "Admin cukup mengunggah dokumen ke folder ini. Subfolder boleh dipakai; namanya menjadi kategori."
              : "Daftar berisi folder yang sudah dibagikan ke email service account."}
        </p>
      </div>
      {folder ? (
        <div className="button-row">
          <button type="button" className="btn btn-ghost" onClick={clear} disabled={busy} aria-busy={busy || undefined}>
            Kosongkan pilihan
          </button>
        </div>
      ) : null}
      {key ? (
        <FolderPicker
          open={picking}
          email={key.clientEmail}
          onClose={() => setPicking(false)}
          onChosen={(next) => {
            setPicking(false);
            onChosen(next);
          }}
        />
      ) : null}
    </section>
  );
}

function HowTo() {
  return (
    <section className="card ks-howto" aria-labelledby="ks-howto-title">
      <h2 id="ks-howto-title" className="card-title">
        Cara menyiapkan di Google Cloud
      </h2>
      <ol className="ks-steps">
        <li>Buka Google Cloud Console, pilih project yang sama dengan login Google aplikasi ini.</li>
        <li>Di APIs &amp; Services &gt; Library, aktifkan Google Drive API.</li>
        <li>Di IAM &amp; Admin &gt; Service Accounts, klik Create service account. Peran (role) tidak perlu diisi.</li>
        <li>Buka service account itu, tab Keys &gt; Add key &gt; Create new key &gt; JSON. File JSON terunduh; simpan di tempat aman.</li>
        <li>Unggah file JSON itu di langkah 1 di atas.</li>
        <li>Di Google Drive, bagikan folder Basis Pengetahuan ke email service account dengan akses Pelihat (Viewer), lalu pilih foldernya di langkah 2.</li>
      </ol>
    </section>
  );
}

export function KnowledgeSourceScreen() {
  useTitle("Sumber Pengetahuan");
  const source = useAsync(() => API.getKnowledgeSource(), []);
  const status = useAsync(() => API.getKnowledgeSourceStatus(), []);
  const audit = useAsync(() => API.listKnowledgeSourceAudit(50), []);
  const saved = source.data;

  function changed(next: KnowledgeSource) {
    source.set(next);
    status.reload();
    audit.reload();
  }

  let body;
  if (source.error) {
    body = (
      <Notice
        tone="danger"
        title="Pengaturan belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={() => source.reload()}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(source.error)}</p>
      </Notice>
    );
  } else if (!saved) {
    body = <SkeletonRows count={3} label="Memuat pengaturan" />;
  } else {
    body = (
      <>
        {!saved.encryptionAvailable ? (
          <Notice tone="warning" title={ENCRYPTION_BANNER}>
            <p>Hubungi pengelola server untuk mengatur kunci enkripsi (SETTINGS_ENCRYPTION_KEY).</p>
          </Notice>
        ) : null}
        <StatusCard status={status} onTested={() => audit.reload()} />
        <KeyCard saved={saved} onSaved={changed} onEncryptionMissing={() => source.set({ ...saved, encryptionAvailable: false })} />
        <FolderCard saved={saved} onChosen={changed} />
        <HowTo />
      </>
    );
  }

  const items = audit.data ?? [];

  return (
    <>
      <PageHeader divided title="Sumber Pengetahuan" lead="Pluto membaca folder Basis Pengetahuan di Google Drive lewat service account. Dokumen di folder itu menjadi pengetahuan Pluto dan muncul di Library." />

      <section className="region" aria-label="Pengaturan sumber pengetahuan">
        <p className="quiet-line">
          <Glyph name="infoCircle" />
          <span>{LOGIN_NOTE}</span>
        </p>
        {body}
      </section>

      <section className="region" aria-labelledby="ks-history-title">
        <h2 id="ks-history-title" className="section-title">
          Riwayat perubahan
        </h2>
        {audit.error ? (
          <Notice tone="danger" title="Riwayat belum termuat">
            <p>{API.errorMessage(audit.error)}</p>
          </Notice>
        ) : audit.loading && !audit.data ? (
          <SkeletonRows count={3} label="Memuat riwayat" />
        ) : items.length === 0 ? (
          <EmptyState icon="history" title="Belum ada perubahan" body="Setiap penyimpanan kunci dan pilihan folder tercatat di sini: siapa, kapan, dan apa yang diubah." />
        ) : (
          <ul className="rows ks-audit" aria-label="Riwayat perubahan">
            {items.map((item) => (
              <li key={item.id}>
                <div className="row">
                  <div className="row-main">
                    <span className="row-title">{item.changedBy?.name ?? "Sistem"}</span>
                    <span className="row-meta">Mengubah {item.fieldsChanged.map((f) => FIELD_LABEL[f] ?? f).join(", ")}</span>
                  </div>
                  <div className="row-side">
                    <span className="row-meta row-now">{formatDateTime(item.changedAt)}</span>
                    {item.ip ? <span className="row-meta">IP {item.ip}</span> : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
